import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { Ticket, X, Trash2, Wallet } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { inr } from "../lib/format";

export const SLIP_QUICK = [100, 200, 500, 1000, 10000];

export const BatIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M15 4l5 5-9 9-5-5z" /><path d="M7 17l-3 3" /></svg>
);

export function StakeBox({ value, onChange, testid }) {
  const filled = Number(value) > 0;
  return (
    <div className={`flex items-center h-10 w-[120px] rounded-lg border-2 bg-white px-2 transition-[border-color,box-shadow] ${filled ? "border-[#f5b400] shadow-[0_0_0_3px_rgba(245,180,0,0.15)]" : "border-slate-300"}`}>
      <span className="text-slate-500 font-bold text-sm mr-1">₹</span>
      <Input type="number" inputMode="numeric" min={0} value={value} onChange={onChange} placeholder="Stake"
        className="border-0 bg-transparent px-0 h-full text-slate-900 font-display font-bold text-base focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
        data-testid={testid} />
    </div>
  );
}

export function SlipChips({ stake, setStake, prefix }) {
  return (
    <div className="mt-2 grid grid-cols-5 gap-1">
      {SLIP_QUICK.map((q) => (
        <button key={q} type="button" onClick={() => setStake(String((Number(stake) || 0) + q))}
          className="h-7 rounded-md bg-[#1f3b5b] hover:bg-[#2a4d75] text-white text-[11px] font-bold tabular-nums active:scale-95 transition-[transform,background-color]"
          data-testid={`${prefix}-${q}`}>₹{q >= 1000 ? `${q / 1000}k` : q}</button>
      ))}
    </div>
  );
}

export function SlipSheet({ onClose, isAuthed, balance, cta, ctaDisabled, onConfirm, confirmTestid, balanceTestid, children, testid = "betslip-sheet", prefix = "betslip" }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true" data-testid={testid}>
      <div className="absolute inset-0 bg-black/75 animate-in fade-in-0 duration-200" onClick={onClose} data-testid={`${prefix}-backdrop`} />
      <div className="relative z-10 w-full sm:max-w-xs flex flex-col max-h-[80dvh] sm:max-h-[85vh] rounded-t-2xl sm:rounded-2xl overflow-hidden bg-[#eef1f5] shadow-2xl animate-in slide-in-from-bottom sm:zoom-in-95 sm:fade-in-0 duration-300 sm:duration-200">
        <div className="relative flex items-center justify-center h-9 shrink-0 bg-[#0b0b0b] border-b border-[#f5b400]/40">
          <span className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-[#f5b400]/70 to-transparent" />
          <span className="font-display font-extrabold text-[11px] uppercase tracking-[0.2em] text-[#f5b400] inline-flex items-center gap-1.5">
            <Ticket className="h-3.5 w-3.5" /> Betslip
            <span className="grid place-items-center h-4 min-w-[16px] px-1 rounded-full bg-[#d81b1b] text-white text-[10px] font-extrabold tracking-normal" data-testid={`${prefix}-count`}>1</span>
          </span>
          <button onClick={onClose} className="absolute right-2 top-1/2 -translate-y-1/2 grid place-items-center h-7 w-7 rounded-full text-slate-300 hover:text-white hover:bg-white/10 transition-colors" data-testid={`${prefix}-close`} aria-label="Close bet slip">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2.5 space-y-2 overscroll-contain">{children}</div>
        <div className="shrink-0 bg-[#eef1f5] px-2.5 pt-1 pb-2.5 space-y-1.5">
          {isAuthed && (
            <div className="flex items-center justify-between text-[11px] text-slate-600">
              <span className="flex items-center gap-1"><Wallet className="h-3.5 w-3.5" /> Balance</span>
              <span className="font-semibold text-slate-900 tabular-nums" data-testid={balanceTestid}>{inr(balance)}</span>
            </div>
          )}
          <Button
            className="w-full h-10 rounded-lg text-[12px] font-display font-extrabold uppercase tracking-wide text-black border-0 bg-[#f5b400] hover:bg-[#ffc21a] shadow-[0_8px_20px_-8px_rgba(245,180,0,0.9)] active:scale-[0.98] transition-[transform,background-color] disabled:opacity-50"
            onClick={onConfirm} disabled={ctaDisabled} data-testid={confirmTestid}>
            {cta}
          </Button>
          <button type="button" onClick={onClose} className="mx-auto flex items-center gap-1 text-[11px] font-semibold text-[#d81b1b] hover:underline" data-testid={`${prefix}-clear`}>
            <Trash2 className="h-3.5 w-3.5" /> Clear Betslip
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
